import React from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import MUIPaper from "@mui/material/Paper";
import MUIStack from "@mui/material/Stack";
import MUITypography from "@mui/material/Typography";
import MUIAlert from "@mui/material/Alert";
import MUIAlertTitle from "@mui/material/AlertTitle";
import background from "../Asset/Image/background.jpg";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      eventId: null,
      crashedAt: null,
      copiedErrorDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { error, crashedAt: new Date().toISOString() };
  }

  /**
   * Capture boundary errors in Sentry and attach React-specific context.
   *
   * @param {Error} error
   * @param {{ componentStack: string }} errorInfo
   */
  componentDidCatch(error, errorInfo) {
    const eventId = Sentry.withScope((scope) => {
      // React component stack is often the fastest way to locate the
      // failing UI path, even when JS stack traces are noisy/minified.
      scope.setContext("react", {
        componentStack: errorInfo.componentStack,
      });

      return Sentry.captureException(error);
    });

    this.setState({ eventId });
  }

  /**
   * Reload the current page after a boundary crash.
   */
  reloadPage = () => {
    window.location.reload();
  };

  /**
   * Open the Sales Pilot support ticket form in a new tab.
   */
  reportIssue = () => {
    window.open(
      "https://rehabmedical.desk365.io/support/form/salespilot/",
      "_blank",
      "noopener,noreferrer",
    );
  };

  /**
   * Build a concise, copy/paste-safe payload for support tickets.
   *
   * @returns {string}
   */
  getErrorDetailsText() {
    const { error, eventId } = this.state;

    return [
      `Reference: ${eventId || "Unavailable"}`,
      `Time: ${this.state.crashedAt || "Unknown"}`,
      `Environment: ${window.salesPilot?.meta?.environment || "Unknown"}`,
      `Version: ${window.salesPilot?.meta?.version || "Unknown"}`,
      `URL: ${window.location.href}`,
      `Error: ${error?.name || "Error"} - ${error?.message || "Unknown error"}`,
    ]
      .filter(Boolean)
      .join("\n");
  }

  /**
   * Copy formatted error details to clipboard for easy ticket submission.
   */
  copyErrorDetails = async () => {
    const details = this.getErrorDetailsText();
    this.setState({ copiedErrorDetails: true });

    try {
      await navigator.clipboard.writeText(details);
      window.setTimeout(
        () => this.setState({ copiedErrorDetails: false }),
        2000,
      );
    } catch (err) {
      this.setState({ copiedErrorDetails: false });
      console.error("Failed to copy error details:", err);
    }
  };

  /**
   * Render the user-facing details panel used in support reports.
   */
  renderErrorDetails() {
    const details = this.getErrorDetailsText();

    return (
      <MUIAlert
        action={
          <MUIButton
            onClick={this.copyErrorDetails}
            size="small"
            type="button"
            variant="outlined"
          >
            {this.state.copiedErrorDetails ? "Copied" : "Copy details"}
          </MUIButton>
        }
        severity="info"
        sx={{ mt: 2 }}
      >
        <MUIAlertTitle>
          Please include this information with your error report
        </MUIAlertTitle>
        <MUIBox
          component="pre"
          sx={{
            fontSize: 12,
            mt: 1.5,
            mb: 0,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            color: "text.primary",
          }}
        >
          {details}
        </MUIBox>
      </MUIAlert>
    );
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <MUIBox
        component="main"
        role="alert"
        sx={{
          "@keyframes salesPilotGustSweep": {
            "0%, 72%, 100%": {
              opacity: 0,
              transform: "translateX(120vw)",
            },
            "73%": {
              opacity: 0.3,
              transform: "translateX(120vw)",
            },
            "80%": {
              opacity: 0.3,
              transform: "translateX(-120vw)",
            },
            "81%": {
              opacity: 0,
              transform: "translateX(-120vw)",
            },
          },
          "@keyframes salesPilotTurbulenceShake": {
            "0%, 75%, 100%": { transform: "translate3d(0, 0, 0) rotate(0deg)" },
            "76%": { transform: "translate3d(-2px, 1px, 0) rotate(-0.2deg)" },
            "77%": { transform: "translate3d(3px, -2px, 0) rotate(0.25deg)" },
            "78%": { transform: "translate3d(-3px, 2px, 0) rotate(-0.2deg)" },
            "79%": { transform: "translate3d(2px, -1px, 0) rotate(0.12deg)" },
            "80%": { transform: "translate3d(-1px, 1px, 0) rotate(-0.1deg)" },
            "81%": { transform: "translate3d(0, 0, 0) rotate(0deg)" },
          },
          alignItems: "center",
          backgroundImage: `url(${background})`,
          backgroundAttachment: "fixed",
          backgroundPosition: "center",
          backgroundSize: "cover",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
          overflow: "hidden",
          p: 4,
          position: "relative",
        }}
      >
        <MUIBox
          sx={{
            animation: "salesPilotGustSweep 40s ease-in-out infinite",
            background:
              "linear-gradient(100deg, rgba(255,255,255,0), rgba(255,255,255,0.38), rgba(255,255,255,0))",
            height: "130vh",
            left: 0,
            pointerEvents: "none",
            position: "absolute",
            top: "-15vh",
            width: "76vw",
            zIndex: 0,
          }}
        />
        <MUIPaper
          elevation={6}
          sx={{
            animation: "salesPilotTurbulenceShake 40s ease-in-out infinite",
            maxWidth: 820,
            p: 5,
            position: "relative",
            transformOrigin: "50% 100%",
            width: "100%",
            zIndex: 1,
          }}
        >
          <MUIStack spacing={2}>
            <MUITypography
              variant="h4"
              sx={{
                color: "primary.dark",
              }}
            >
              We hit some unexpected turbulence.
            </MUITypography>
            <MUITypography variant="body1">
              Sorry, this page stopped working. The technical details have been
              sent to our team so we can investigate.
            </MUITypography>
            <MUITypography variant="body1">
              You can reload Sales Pilot and try again. If this keeps happening,
              please report the issue and include the information below along
              with any other details about what you were doing.
            </MUITypography>
            <MUIStack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ pt: 1 }}
            >
              <MUIButton
                onClick={this.reloadPage}
                type="button"
                variant="contained"
              >
                Reload Sales Pilot
              </MUIButton>
              <MUIButton
                onClick={this.reportIssue}
                type="button"
                variant="outlined"
              >
                Report issue
              </MUIButton>
            </MUIStack>
            {this.renderErrorDetails()}
          </MUIStack>
        </MUIPaper>
      </MUIBox>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string,
};

ErrorBoundary.defaultProps = {
  name: "global",
};

export default ErrorBoundary;
